/**
 * Security Provider - Global security enhancements for the application
 */
import { useEffect } from 'react';
import { initializeSecurity } from '@/utils/securityEnforcement';
import { SecurityMonitor } from '@/utils/securityMonitoring';

export function SecurityProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Initialize security monitoring and enforcement
    initializeSecurity();
    
    // Log application security initialization
    SecurityMonitor.logEvent('info', 'security_initialized', 'app', {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent.slice(0, 100), // Truncated for privacy
      location: window.location.pathname
    });

    // Enhanced Content Security Policy enforcement
    const meta = document.createElement('meta');
    meta.httpEquiv = 'Content-Security-Policy';
    meta.content = [
      "default-src 'self'",
      "script-src 'self' 'unsafe-inline'", // Only for JSON-LD structured data
      "style-src 'self' 'unsafe-inline'",
      "img-src 'self' data: https:",
      "font-src 'self'",
      "connect-src 'self' https://uqjjisbrbasocuhmbviz.supabase.co",
      "frame-ancestors 'none'",
      "base-uri 'self'",
      "form-action 'self'"
    ].join('; ');
    
    // Only add CSP if not already present
    if (!document.querySelector('meta[http-equiv="Content-Security-Policy"]')) {
      document.head.appendChild(meta);
    }

    // Security headers simulation for client-side
    const securityHeaders = {
      'X-Content-Type-Options': 'nosniff',
      'X-Frame-Options': 'DENY',
      'X-XSS-Protection': '1; mode=block',
      'Referrer-Policy': 'strict-origin-when-cross-origin'
    };

    // Log security configuration
    SecurityMonitor.logEvent('info', 'security_headers_configured', 'app', {
      headers: Object.keys(securityHeaders).join(', ')
    });

    // Enhanced error monitoring
    const originalOnError = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      // Log security-relevant errors
      SecurityMonitor.logEvent('error', 'javascript_error', 'app', {
        message: typeof message === 'string' ? message.slice(0, 200) : 'Unknown error',
        source: source?.slice(0, 100),
        line: lineno,
        column: colno
      });
      
      // Call original handler if it exists
      if (originalOnError) {
        return originalOnError(message, source, lineno, colno, error);
      }
      return false;
    };

    // Monitor for potential security violations
    const handleSecurityViolation = (event: SecurityPolicyViolationEvent) => {
      SecurityMonitor.logSecurityViolation('csp_violation', 'app', {
        violatedDirective: event.violatedDirective,
        blockedURI: event.blockedURI?.slice(0, 200),
        documentURI: event.documentURI?.slice(0, 200)
      });
    };

    // Listen for CSP violations
    document.addEventListener('securitypolicyviolation', handleSecurityViolation);

    return () => {
      document.removeEventListener('securitypolicyviolation', handleSecurityViolation);
      window.onerror = originalOnError;
    };
  }, []);

  return <>{children}</>;
}